<script setup>
import CanvasOrganism from '@/components/organisms/CanvasOrganism.vue';
import { useCanvas } from '@/composables/canvas';
import { useOCR } from '@/composables/useOCR'; 
import { defineProps, defineEmits, ref } from 'vue';

const props = defineProps(["question"])
const emit = defineEmits(["result", "done"])

const canvasStore = useCanvas()

// 1. Init Tesseract Composable
const { recognize, text, status, progress, error } = useOCR();

const showResult = ref(false)
const showOriginal = ref(false)
const audioPlayer = ref(null)

const Check = async () => {
  emit("done")
  showResult.value = true;

  // 2. Capture Canvas
  const base64Img = canvasStore.asBASE64();

  console.log("img", base64Img)

  // 3. Trigger Local OCR (German language 'deu')
  // This is async, but we can rely on the 'status' ref for UI updates
  await recognize(base64Img, 'deu');

  // 4. Emit result once complete
  if (status.value === 'completed') {
    emit("result", text.value);
  }
}

const play = () => {
  if (audioPlayer.value) {
    audioPlayer.value.currentTime = 0;
    audioPlayer.value.play();
  }
}

const closeResult = () => {
  showResult.value = false;
  // Optional: clear canvas if desired
  // canvasStore.clear(); 
}
</script>

<template>
  <audio 
    ref="audioPlayer" 
    :src="props.question?.audio" 
    style="display: none;"
  ></audio>

  <div class="container">
    <div class="word" v-if="showOriginal">
      {{ props.question.word }}
    </div>
    
    <div class="canvas-wrapper">
      <canvas-organism class="canvas"></canvas-organism>
    </div>
  </div>

  <div class="result-overlay" v-if="showResult">
    <div class="result-box">
      
      <div v-if="status === 'recognizing' || status === 'loading'" class="loading-state">
        <h3>Analysiere...</h3>
        <progress :value="progress" max="100"></progress>
        <p>{{ Math.round(progress) }}%</p>
      </div>

      <div v-else-if="status === 'error'" class="error-state">
        <h3>Fehler</h3>
        <p>{{ error }}</p>
        <div class="button secondary" @click="closeResult">Schließen</div>
      </div>

      <div v-else class="success-state">
        <h3>Ergebnis:</h3>
        <div class="result-text">
          {{ text || "Kein Text erkannt" }}
        </div>
        <div class="button secondary" @click="closeResult">Ok</div>
      </div>

    </div>
  </div>

  <div class="buttons" v-if="showResult == false">
    <div class="button secondary" @click="canvasStore.clear()">Löschen</div>
    <div class="button secondary" @click="play()">Abspielen</div>
    <div class="button success" @click="Check()">Fertig</div>
  </div>
</template>

<style scoped lang="scss">
/* --- Container --- */
.container {
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  position: relative;

  .canvas-wrapper {
    flex-grow: 1;
    position: relative;
    width: 100%;
    height: 100%;
  }

  .canvas {
    height: 100%;
    width: 100%;
  }

  .word {
    height: 60px;
    font-size: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.6);
  }
}

/* --- Result Overlay --- */
.result-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.result-box {
  background-color: white;
  width: 400px;
  padding: 30px;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  h3 {
    margin-top: 0;
    color: #333;
  }

  /* Text Result Styling */
  .result-text {
    font-size: 2rem;
    font-weight: bold;
    color: #2c3e50;
    margin: 20px 0;
    min-height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    word-break: break-word;
  }

  progress {
    width: 100%;
    height: 10px;
    border-radius: 5px;
    margin: 10px 0;
    
    &::-webkit-progress-bar {
      background-color: #eee;
      border-radius: 5px;
    }
    &::-webkit-progress-value {
      background-color: var(--color-status-success, #4caf50);
      border-radius: 5px;
    }
  }

  .button.secondary {
    margin-top: 10px;
    width: 150px;
    height: 50px;
    border-radius: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #eee;
    cursor: pointer;
    font-weight: bold;
    
    &:hover {
      background-color: #ddd;
    }
  }
}

/* --- Bottom Buttons --- */
.buttons {
  position: absolute;
  right: var(--spacing-xl);
  bottom: var(--spacing-xl);
  display: flex;
  z-index: 10;

  .button {
    width: calc(var(--spacing-lg)*4);
    height: calc(var(--spacing-lg)*4);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: calc(var(--spacing-lg)*2);
    font-size: var(--font-size-lg);
    margin-left: var(--spacing-lg);
    cursor: pointer;
    user-select: none;
    transition: transform 0.1s;

    &:active {
      transform: scale(0.95);
    }

    &.success {
      color: var(--color-text-dark);
      background-color: var(--color-status-success);
      box-shadow: 0 4px 10px rgba(0,0,0,0.1);
    }

    &.secondary {
      color: var(--color-accent);
      background-color: var(--color-secondary);
      box-shadow: 0 4px 10px rgba(0,0,0,0.1);
    }
  }
}
</style>